import { useContext } from 'react';

import { BetaPreferencesContext } from './BetaPreferencesProvider';
import type { BetaKey } from './constants';

/**
 * Group-level controller: the available keys, whether any entry point should show,
 * the in-flight state, and the write action. Used by surfaces that render the whole
 * set (e.g. Settings) and to gate visibility of the entry point.
 */
export function useBetaController() {
  const {
    keys, isAvailable, isPending, setEnabled,
  } = useContext(BetaPreferencesContext);
  return {
    keys,
    isAvailable,
    isPending,
    setEnabled,
  };
}

/**
 * Per-key selector for a single toggle, used by surfaces that render a known toggle
 * (the agent menu) or a single Settings row. `disabled` reflects the global in-flight
 * lock; `loading` is true only for the key currently being written.
 *
 * `key` may be undefined so route-derived surfaces (the beta banner, which reads its key from
 * the route group matching the current pathname) can call the hook unconditionally. With no key
 * there is nothing to toggle, so the toggle reports `available: false` and `setEnabled` no-ops.
 */
export function useBetaToggle(key: BetaKey | undefined) {
  const {
    keys, isPending, loadingKey, isChecked, isReadOnly, setEnabled,
  } = useContext(BetaPreferencesContext);
  const readOnly = key !== undefined && isReadOnly(key);
  return {
    available: key !== undefined && keys.includes(key),
    checked: key !== undefined && isChecked(key),
    // Global lock (FRONTEND-658): disabled while any write is pending, or locked read-only.
    disabled: isPending || readOnly,
    loading: key !== undefined && loadingKey === key,
    readOnly,
    setEnabled: (enabled: boolean) => (key === undefined ? undefined : setEnabled(key, enabled)),
  };
}
